import React, { createContext, ReactNode, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import {
  clearUserInfoCache,
  getCachedUserInfo,
  ImgCloudUserConfig,
  ImgCloudUserInfo,
  loadUserInfo,
  UserInfoSnapshot,
  UserInfoStatus,
} from '../services/userInfo';

export interface UserInfoContextValue {
  config: ImgCloudUserConfig;
  dailyMaxUploadCount: number | string | null;
  enableAiUrlFetch: boolean;
  error: Error | null;
  isActiveVip: boolean;
  isAuthenticated: boolean;
  isRefreshing: boolean;
  refresh: () => Promise<void>;
  clear: () => void;
  status: UserInfoStatus;
  user: ImgCloudUserInfo | null;
}

interface UserInfoState {
  error: Error | null;
  isRefreshing: boolean;
  snapshot: UserInfoSnapshot | null;
  status: UserInfoStatus;
}

export const UserInfoContext = createContext<UserInfoContextValue | null>(null);

const createInitialState = (): UserInfoState => {
  const snapshot = getCachedUserInfo();
  return {
    error: null,
    isRefreshing: false,
    snapshot,
    status: snapshot?.status || 'loading',
  };
};

const syncLegacyWindowUserInfo = (snapshot: UserInfoSnapshot | null): void => {
  const targetWindow = window as typeof window & { _info?: ImgCloudUserInfo };
  if (snapshot?.status === 'authenticated' && snapshot.user) {
    targetWindow._info = snapshot.user;
  } else {
    delete targetWindow._info;
  }
};

export const UserInfoProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [state, setState] = useState<UserInfoState>(createInitialState);
  const requestSequenceRef = useRef(0);

  const applySnapshot = useCallback((snapshot: UserInfoSnapshot) => {
    syncLegacyWindowUserInfo(snapshot);
    setState({
      error: null,
      isRefreshing: false,
      snapshot,
      status: snapshot.status,
    });
  }, []);

  const runLoad = useCallback(async (force: boolean) => {
    const requestSequence = ++requestSequenceRef.current;
    setState((current) => ({
      ...current,
      error: null,
      isRefreshing: force && current.snapshot !== null,
      status: current.snapshot ? current.status : 'loading',
    }));

    try {
      const snapshot = await loadUserInfo({ force });
      if (requestSequence === requestSequenceRef.current) {
        applySnapshot(snapshot);
      }
    } catch (error) {
      if (requestSequence !== requestSequenceRef.current) {
        return;
      }
      const normalizedError = error instanceof Error ? error : new Error('Failed to load user information');
      setState((current) => ({
        ...current,
        error: normalizedError,
        isRefreshing: false,
        status: current.snapshot ? current.status : 'error',
      }));
    }
  }, [applySnapshot]);

  useEffect(() => {
    let active = true;
    const requestSequence = ++requestSequenceRef.current;

    loadUserInfo()
      .then((snapshot) => {
        if (active && requestSequence === requestSequenceRef.current) {
          applySnapshot(snapshot);
        }
      })
      .catch((error) => {
        if (!active || requestSequence !== requestSequenceRef.current) {
          return;
        }
        const normalizedError = error instanceof Error ? error : new Error('Failed to load user information');
        setState((current) => ({
          ...current,
          error: normalizedError,
          isRefreshing: false,
          status: current.snapshot ? current.status : 'error',
        }));
      });

    return () => {
      active = false;
    };
  }, [applySnapshot]);

  const refresh = useCallback(() => runLoad(true), [runLoad]);

  const clear = useCallback(() => {
    requestSequenceRef.current += 1;
    clearUserInfoCache();
    syncLegacyWindowUserInfo(null);
    setState({
      error: null,
      isRefreshing: false,
      snapshot: {
        config: {},
        dailyMaxUploadCount: null,
        enableAiUrlFetch: false,
        status: 'anonymous',
        user: null,
      },
      status: 'anonymous',
    });
  }, []);

  const contextValue = useMemo<UserInfoContextValue>(() => ({
    clear,
    config: state.snapshot?.config || {},
    dailyMaxUploadCount: state.snapshot?.dailyMaxUploadCount ?? null,
    enableAiUrlFetch: state.snapshot?.enableAiUrlFetch === true,
    error: state.error,
    isActiveVip: state.snapshot?.user?.isActiveVip === true,
    isAuthenticated: state.status === 'authenticated',
    isRefreshing: state.isRefreshing,
    refresh,
    status: state.status,
    user: state.snapshot?.user || null,
  }), [clear, refresh, state]);

  return (
    <UserInfoContext.Provider value={contextValue}>
      {children}
    </UserInfoContext.Provider>
  );
};

export const useUserInfo = (): UserInfoContextValue => {
  const context = useContext(UserInfoContext);
  if (!context) {
    throw new Error('useUserInfo must be used within UserInfoProvider');
  }
  return context;
};
